/**
 * @Description 折线柱状组合图
 */
import React, { useRef, useEffect, useState } from 'react';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { SVGRenderer } from 'echarts/renderers';
import { GridComponent, TooltipComponent, TitleComponent, LegendComponent } from 'echarts/components';
// 标签自动布局、全局过渡动画等特性
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { yAxisFormatter } from '@lhb/func';
import { EChartsOption } from 'echarts';
import styles from '@/common/components/Charts/V2BarLineChart/index.module.less';
import { unstable_batchedUpdates } from 'react-dom';
import cs from 'classnames';
// import { themeColor } from '@/common/components/config-v2';

echarts.use([
  SVGRenderer,
  LineChart,
  BarChart,
  GridComponent,
  TooltipComponent,
  TitleComponent,
  LegendComponent,
  LabelLayout,
  UniversalTransition,
]);

export interface V2BarLineChartProps {
  /**
   * @description 图表标题
   */
  title?: string;
  /**
   * @description 图表主题
   */
  theme?: 'blue' | 'green' | 'purple';
  /**
   * @description 图表宽度，默认不指定，跟随外容器
   */
  width?: number | string;
  /**
   * @description 图表高度
   * @default 270px
   */
  height?: number | string;
  /**
   * @description x轴数据 更多配置见 https://echarts.apache.org/zh/option.html#xAxis
   */
  xAxisData: string[];
  /**
   * @description 图表数据 更多配置见 https://echarts.apache.org/zh/option.html#series-line
   */
  seriesData: any[];
  /**
   * @description y坐标轴数据，左右两边各一条坐标轴 更多配置见 https://echarts.apache.org/zh/option.html#yAxis
   */
  yAxisData?: any[];
  /**
   * @description echarts更多配置 详情见https://echarts.apache.org/zh/option.html#title
   * @type EChartsOption
   */
  config?: EChartsOption;
  /**
   * @description 图表外层包装容器的类名
   */
  wrapperClassName?: string;
}

/**
* @description 便捷文档地址
* @see https://reactmobile.lanhanba.net/charts/v2-bar-line-chart
*/

const PPTBarLineChart: React.FC<V2BarLineChartProps> = (props) => {
  const {
    title,
    // theme = 'blue',
    width,
    height = 270,
    xAxisData = [],
    seriesData = [],
    yAxisData = [],
    config = {},
    wrapperClassName,
  } = props;
  const chartRef = useRef<any>(null);
  let chart: any;

  const seriesBarBaseConfig = {
    type: 'bar',
    smooth: true,
    showSymbol: false,
    itemStyle: {},
    barWidth: 22,
    label: {
      show: false,
      position: 'top',
      color: '#999999',
      fontSize: 10,
      formatter: (obj: any) => yAxisFormatter(obj.data)
    },
  };
  const seriesLineBaseConfig = {
    type: 'line',
  };

  // const seriesAreaStyleConfig = (index: number) => {
  //   return {
  //     color: {
  //       type: 'linear',
  //       x: 0,
  //       y: 0,
  //       x2: 0,
  //       y2: 1,
  //       colorStops: [{
  //         offset: 0, color: themeColor[theme][index],
  //       }, {
  //         offset: 1, color: `rgba(${themeColor[`${theme}RGB`][index]}, 0.5)`, // 50%透明度
  //       }],
  //       global: false,
  //     },
  //   };
  // };

  // 横轴配置
  const xAxis = {
    type: 'category',
    data: [],
    nameTextStyle: {
      fontSize: 16,
      fontFamily: 'PingFangSC, PingFang SC',
      color: '#222222',
      lineHeight: 22
    },
    axisLine: {
      show: false,
      lineStyle: {
        color: '#999',
      },
    },
    axisLabel: {
      color: '#222222',
      lineHeight: 22,
      fontSize: 14,
      fontWeight: 400
    },
    axisTick: {
      show: false,
    },
    ...(config.xAxis || {}),
  };
  // 竖轴配置
  const yAxis = {
    type: 'value',
    axisLabel: {
      formatter: yAxisFormatter,
      axisLabel: {
        color: '#222222',
        lineHeight: 22,
        fontSize: 16,
        fontWeight: 400
      }
    },
    nameTextStyle: {
      fontSize: 16,
      fontFamily: 'PingFangSC, PingFang SC',
      color: '#999999',
      lineHeight: 22
    },
    axisLine: {
      lineStyle: {
        color: '#999',
      },
    },
    axiesLabel: {
      fontSize: 16,
      fontFamily: 'PingFangSC, PingFang SC',
      color: '#999999',
      lineHeight: 22
    },
    splitLine: {
      lineStyle: {
        color: ['#eee'],
        width: 0.5,
      },
    }
  };


  const [isConfig, setIsConfig] = useState(false); // options 是否配置完成
  const [options, setOptions] = useState<any>({
    title: {
      show: !!title,
      text: title || '',
      textStyle: {
        color: '#222222',
        fontWeight: 500,
        fontFamily: 'PingFangSC-Medium, PingFang SC',
        fontSize: 18,
        lineHeight: 22,
      },
      top: 4,
      left: 5,
      ...(config.title || {}),
    },
    legend: {
      type: 'plain',
      width: '75%',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 22,
      itemStyle: {
        borderWidth: 0,
      },
      textStyle: {
        fontSize: 14,
        fontWeight: 400,
        color: '#666666',
        lineHeight: 22,
        rich: {
          a: {
            verticalAlign: 'middle',
          },
        },
        padding: [0, 4, 1, 0],
      },
      icon: 'roundRect',
      right: 5,
      top: 5,
      ...(config.legend || {}),
    },
    // color: themeColor[theme],
    grid: {
      left: 40,
      top: 50,
      right: 60,
      bottom: 30,
      ...config.grid
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: {
        type: 'shadow',
      },
      backgroundColor: '#222',
      borderColor: '#222',
      borderWidth: 0,
      textStyle: {
        color: '#fff',
        fontWeight: 'normal',
        fontSize: 12,
        fontFamily: 'PingFangSC-Regular, PingFang SC',
      },
      confine: true,
      alwaysShowContent: false,
      ...(config.tooltip || {}),
    },
    xAxis,
    yAxis: [{ ...yAxis, ...(yAxisData?.[0] || {}) }, { ...yAxis, ...(yAxisData?.[1] || {}) }],
    series: [],
  });

  useEffect(() => {
    if (xAxisData?.length && seriesData?.length) {
      unstable_batchedUpdates(() => {
        setOptions({
          ...options,
          series: seriesData.map((item) => {
            return {
              ...(item.type === 'bar' ? seriesBarBaseConfig : seriesLineBaseConfig),
              data: item.data,
              itemStyle: {
                // ...seriesAreaStyleConfig(index),
                ...item.itemStyle
              },
              ...item,
              label: {
                ...seriesBarBaseConfig.label,
                ...item.label || {},
              },
            };
          }),
          grid: {
            ...options.grid,
            right: seriesData.filter((item) => item.yAxisIndex === 1).length ? 50 : 12,
          },
          xAxis: {
            ...options.xAxis,
            data: xAxisData,
          },
        });
        setIsConfig(true);
      });
    }
  }, [xAxisData, seriesData]);

  useEffect(() => {
    if (!isConfig) return;
    if (chartRef.current) {
      chart = echarts.init(chartRef.current, 'light', {
        renderer: 'svg',
        height,
      });
      chart.setOption(options);
    }
    return () => chart?.dispose();
  }, [options, isConfig]);

  return (
    <div className={cs(styles.V2BarLineChartWrap, wrapperClassName)} style={{ width, height }}>
      <div ref={chartRef} className={styles.V2BarLineChart}></div>
    </div>
  );
};

export default PPTBarLineChart;
